import React from 'react'
import styled from 'styled-components'
import { useAccountSAKMutation, useTrackMutation } from '@mote/api'
import { BASE_URL, TRACK_EVENT_NAMES } from '@mote/helpers/constants'
import { Block, BlockGroup, H3, H4, H5 } from '@mote/ui'
import { useAuth } from '../../auth/useAuth'
import { AdminLayout } from '../../layouts/Admin/admin.layout'
import {
  broadcastToExtensions,
  getMoteLogin,
  getReferrerCodeURL
} from '../../shared/Auth'
import { getTZOffset } from '../../shared/Utils'
import { GoalsItem } from './GoalsItem'
import { JSONDisplay } from './JSONDisplay'
import { enforceAdministratorOnlyAccess } from './adminHelpers'

export const MOTE_DEBUG_US_SCHOOLS_STORAGE_KEY = 'mote-debug-us-schools'
export const MOTE_DEBUG_US_SCHOOLS_LABS_MODE_STORAGE_KEY =
  'mote-debug-us-schools-labs-mode'

const REFRESH_DELAY_TIME = 500

export const AdminDebugSAKScreen = () => {
  const { user, updateUser } = useAuth()
  const [usSchoolSearchMode, setUsSchoolSearchMode] = React.useState(
    window.localStorage.getItem(MOTE_DEBUG_US_SCHOOLS_STORAGE_KEY)
  )
  const [usSchoolSearchLabsMode, setUsSchoolSearchLabsMode] = React.useState(
    window.localStorage.getItem(MOTE_DEBUG_US_SCHOOLS_LABS_MODE_STORAGE_KEY)
  )

  const { mutate: accountSAK, data, error, loading } = useAccountSAKMutation()
  const { mutate: trackMutation } = useTrackMutation()

  function track(...args) {
    return trackMutation(...args).then(
      () =>
        new Promise(resolve => {
          const id = setTimeout(() => {
            clearTimeout(id)
            resolve()
          }, REFRESH_DELAY_TIME)
        })
    )
  }

  React.useEffect(() => {
    enforceAdministratorOnlyAccess()
  }, [])

  React.useEffect(() => {
    accountSAK({
      variables: {
        mode: 'no_op',
        TZOffset: getTZOffset()
      }
    }).then(res => {
      if (res.data) {
        updateUser(getMoteLogin())
      }
    })
  }, [])

  const userState = React.useMemo(() => {
    if (!user) return {}

    const isPaid = Boolean(user.isPaid)
    const isUpgradable = Boolean(user.isUpgradable)
    const product = user.productName || 'unknown'

    return {
      isPaid,
      isUpgradable,
      product
    }
  }, [user])

  function switchMode(e, mode, options = {}) {
    e.preventDefault()

    accountSAK({
      variables: {
        mode,
        TZOffset: getTZOffset(),
        ...options
      }
    }).then(() => updateUser(getMoteLogin()))
  }

  return (
    <AdminLayout
      title={<H3 alignment='center'>DebugSAK</H3>}
      containerVariant='wide'
    >
      <BlockGroup layout='horizontal' gap={50} justify='space-between' spreadX>
        <Block maxWidth={550} gap={30} spreadX>
          <H4>Change or query state</H4>
          <Actions>
            <H5>Change product enums</H5>

            <li>
              <a href='/' onClick={e => switchMode(e, 'reset')}>
                Reset account to factory defaults
              </a>
              <br />
              (sets goals.* to 'required')
            </li>

            <li>
              <a href='/' onClick={e => switchMode(e, 'free_trial_end')}>
                End free trial
              </a>
            </li>

            <li>
              <a href='/' onClick={e => switchMode(e, 'free_trial_start')}>
                Start free trial
              </a>
            </li>

            <li>
              <a
                href='/'
                onClick={e =>
                  switchMode(e, 'fake_purchase', { productEnum: 'unlimited' })
                }
              >
                Fake B2C (Unlimited) purchase
              </a>
            </li>

            <li>
              <a
                href='/'
                onClick={e =>
                  switchMode(e, 'fake_school_purchase', { unlimited: 100 })
                }
              >
                Fake B2B (100 Unlimited seats) purchase
              </a>
              <br />
              (you are the owner)
            </li>

            <li>
              <a
                href='/'
                onClick={e =>
                  switchMode(e, 'fake_school_purchase_by_other_as_admin')
                }
              >
                Fake B2B (20 Unlimited seats) purchase
              </a>
              <br />
              (you are an admin but NOT the owner)
            </li>

            <li>
              <a
                href='/'
                onClick={e =>
                  switchMode(e, 'fake_school_purchase_by_other_not_as_admin')
                }
              >
                Fake B2B (20 Unlimited seats) purchase
              </a>
              <br />
              (you are NEITHER an admin nor an owner, just a normal user)
            </li>

            <li>
              <a href='/' onClick={e => switchMode(e, 'free_even_at_xmas')}>
                Make free even during December promotional upgrade periods
              </a>
            </li>

            <br />
            <H5>Lineker testing</H5>

            <li>
              <a
                href='/'
                onClick={async e => {
                  e.preventDefault()
                  broadcastToExtensions(
                    'reset_lineker_seen_flag',
                    {},
                    response => {
                      switchMode(e, 'no_op')
                    }
                  )
                }}
              >
                Reset Lineker survey seen storage flag
              </a>
            </li>

            <li>
              <a href='/' onClick={e => switchMode(e, 'reset_lineker_survey')}>
                Enable survey (by setting goals.linekerSurvey to 'required')
              </a>
            </li>

            <li>
              <a
                href='/'
                onClick={e => {
                  e.preventDefault()
                  track({
                    variables: {
                      event: TRACK_EVENT_NAMES.goals.completed,
                      goalName: 'linekerSurvey'
                    }
                  }).then(() => switchMode(e, 'no_op'))
                }}
              >
                Disable survey (by calling track('Goal Completed', &#123;
                goalName: 'linekerSurvey'&#125;))
              </a>
            </li>

            <GoalsItem
              goalName={'loopOptIn'}
              goalStatus={'required'}
              onUpdateSuccess={e => switchMode(e, 'no_op')}
              text={'Enable MOTE:LOOP'}
            />
            <GoalsItem
              goalName={'loopOptIn'}
              goalStatus={'skip_for_now'}
              onUpdateSuccess={e => switchMode(e, 'no_op')}
              text={'Disable MOTE:LOOP'}
            />

            <li>
              <a href='/' onClick={e => switchMode(e, 'reset_meme_carousels')}>
                Reset meme carousels (affirmative and assistive)
              </a>
            </li>

            <br />
            <H5>Welcome flow 2.0 testing</H5>

            <li>
              <a
                href='/'
                onClick={e => {
                  e.preventDefault()
                  setUsSchoolSearchMode(prev => {
                    window.localStorage.setItem(
                      MOTE_DEBUG_US_SCHOOLS_STORAGE_KEY,
                      prev === 'true' ? 'false' : 'true'
                    )

                    return prev === 'true' ? 'false' : 'true'
                  })
                }}
              >
                {usSchoolSearchMode === 'true' ? 'Unforce' : 'Force'} US schools
                search
              </a>
            </li>

            <li>
              <a
                href='/'
                onClick={e => {
                  e.preventDefault()
                  setUsSchoolSearchLabsMode(prev => {
                    window.localStorage.setItem(
                      MOTE_DEBUG_US_SCHOOLS_LABS_MODE_STORAGE_KEY,
                      prev === 'true' ? 'false' : 'true'
                    )

                    return prev === 'true' ? 'false' : 'true'
                  })
                }}
              >
                {usSchoolSearchLabsMode === 'true' ? 'Unforce' : 'Force'} US
                schools search with labs mode
              </a>
            </li>

            <li>
              <a
                href='/'
                onClick={e => switchMode(e, 'fake_institution_name_hints')}
              >
                Fake populate institutionNameHints
              </a>
            </li>

            <li>
              <a
                href='/'
                onClick={e => switchMode(e, 'no_institution_name_hints')}
              >
                No institutionNameHints
              </a>
              <br />
              (as if this is the first user in a domain)
            </li>

            <li>
              <a
                href='/'
                onClick={e => switchMode(e, 'default_institution_name_hints')}
              >
                Revert to default institutionNameHints
              </a>
              <br />
              (results may be a bit random)
            </li>

            <li>
              <a
                href='/'
                onClick={e => switchMode(e, 'make_post_welcome_flow_2_0')}
              >
                Force new style user
              </a>
              <br />
              (goals.userOnboarding will be 'required' or 'completed', and never
              'skip_for_now')
            </li>

            <li>
              <a
                href='/'
                onClick={e => switchMode(e, 'make_pre_welcome_flow_2_0')}
              >
                Force old style user (pre Welcome Flow 2.0)
              </a>
              <br />
              (goals.userOnboarding will be 'skip_for_now' or 'completed')
            </li>

            <li>
              <a
                href='/'
                onClick={e => {
                  e.preventDefault()
                  track({
                    variables: {
                      event: TRACK_EVENT_NAMES.goals.completed,
                      goalName: 'userOnboarding'
                    }
                  }).then(() => switchMode(e, 'no_op'))
                }}
              >
                Call track('Goal Completed', &#123; goalName:
                'userOnboarding'&#125;)
              </a>
            </li>

            <li>
              <a
                href='/'
                onClick={e => {
                  e.preventDefault()
                  track({
                    variables: {
                      event: TRACK_EVENT_NAMES.goals.completed,
                      goalName: 'productOnboarding'
                    }
                  }).then(() => switchMode(e, 'no_op'))
                }}
              >
                Call track('Goal Completed', &#123; goalName:
                'productOnboarding'&#125;)
              </a>
            </li>

            <li>
              <a href='/' onClick={e => switchMode(e, 'adult')}>
                Make 18+ (adult)
              </a>
            </li>

            <br />
            <H5>Exercise free moting limits</H5>

            <li>
              <a href='/' onClick={e => switchMode(e, 'quota_less_6')}>
                Fake record 14 motes
              </a>
            </li>

            <li>
              <a href='/' onClick={e => switchMode(e, 'quota_less_5')}>
                Fake record 15 motes
              </a>
            </li>
            <li>
              <a href='/' onClick={e => switchMode(e, 'quota_less_1')}>
                Fake record 19 motes
              </a>
            </li>
            <li>
              <a href='/' onClick={e => switchMode(e, 'quota_all_used_up')}>
                Fake record all free motes
              </a>
            </li>

            <br />
            <H5>Misc</H5>

            <li>
              <a href='/' onClick={e => switchMode(e, 'fake_referral')}>
                Fake referral
              </a>
            </li>
            <li>
              <a href='/' onClick={e => switchMode(e, 'reset_referral_state')}>
                Reset referral state
              </a>
            </li>

            <li>
              <a
                href='/'
                onClick={e => switchMode(e, 'expire_access_token_30s')}
              >
                Expire token in 30 seconds
              </a>
            </li>
            <li>
              <a
                href='/'
                onClick={e =>
                  switchMode(e, 'refresh_access_token_on_next_usage')
                }
              >
                Force access token refresh
              </a>
              <br />
              (create new access token on first usage; the current one is still
              valid, but clients should discard it)
            </li>
            <li>
              <a
                href='/'
                onClick={e => switchMode(e, 'send_activity_notification')}
              >
                Send activity notification
              </a>
            </li>

            <li>
              <a
                href='/'
                onClick={e =>
                  switchMode(e, 'send_weekly_activity_summary_event')
                }
              >
                Send 'Weekly Activity Summary' event
              </a>
            </li>

            <li>
              <a target='_new' href={BASE_URL + '/c/geo'}>
                GeoIP back-end result
              </a>
            </li>
          </Actions>
        </Block>

        <Block gap={10} maxWidth={550} spreadX>
          <H5>Current state</H5>
          {loading && <div>Processing...</div>}
          {!loading && user && (
            <CurrentState>
              <p>
                <b>Email:</b> {user.primaryEmailAddress}{' '}
              </p>
              <p>
                <b>userId:</b> {user.userId}{' '}
              </p>
              <p>
                <b>Referrer code:</b> {user.referrerCode}{' '}
                {getReferrerCodeURL() && (
                  <a href={getReferrerCodeURL()}>{getReferrerCodeURL()}</a>
                )}
              </p>

              <p>
                <b>Current product:</b> {userState.product}{' '}
              </p>
              <p>
                <b>Country code</b> {user.countryCode}{' '}
              </p>

              <p>
                <b>Is paid up?</b> {userState.isPaid ? 'true' : 'false'}{' '}
              </p>
              <p>
                <b>Is upgradable?</b>{' '}
                {userState.isUpgradable ? 'true' : 'false'}{' '}
              </p>
              <p>
                <b>Is minor?</b> {user.isMinor ? 'true' : 'false'}{' '}
              </p>

              <p>
                <b>canMote?</b> {user.canMote ? 'true' : 'false'}{' '}
              </p>

              <p>
                <b>canUseBook?</b> {user.canUseBook ? 'true' : 'false'}{' '}
              </p>

              <p>
                <b>isInstitutional:</b>{' '}
                {user.isInstitutional ? 'true' : 'false'}
              </p>

              <div>
                <b>Goals:</b>
                <JSONDisplay obj={data?.goals} />
              </div>

              <div>
                <b>institutionNameHints:</b>
                <JSONDisplay obj={user.institutionNameHints} />
              </div>

              <div>
                <b>Legals:</b> {user.legals}
              </div>

              <div>
                <b>Survey response:</b>
                <JSONDisplay obj={data?.accountSAK.surveyResponse} />
              </div>

              <div>
                <b>Notification preferences:</b>
                <JSONDisplay obj={data?.accountSAK.notificationPreferences} />
              </div>

              <div>
                <b>Affirmative carousel:</b>
                <JSONDisplay obj={user.affirmativeCarousel} />
              </div>

              <div>
                <b>Assistive carousel:</b>
                <JSONDisplay obj={user.assistiveCarousel} />
              </div>

              {error && (
                <p>
                  <font color='red'>
                    <b>Error:</b> {error.toString()}{' '}
                  </font>
                </p>
              )}
            </CurrentState>
          )}
        </Block>
      </BlockGroup>
    </AdminLayout>
  )
}

const CurrentState = styled.div`
  border-radius: 20px;
  padding: 20px;
  background: #f9f9fb;
  margin-top: 20px;
  word-break: break-all;
`

const Actions = styled.ul`
  list-style-type: none;
  padding-inline-start: 0;
  margin: 0;

  a {
    color: #ac0ae8;
  }
`
